// packages/kb-view/src/docs-slug.ts
//
// LAT-1606 (LAT-1600 S6) — the `/docs` slug grammar ADR 0049 §1 pins, in the
// one place both of its enforcement points read it from.
//
// ADR 0049 §1 requires the grammar be "enforced twice": the content-law gate
// (`tools/scripts/help-content.test.ts`) refuses to merge a page or a nav
// manifest entry the family could not serve, and the docs worker's router
// (LAT-1607) parses incoming request paths with the same predicate. Enforcing
// it twice from two copies of the regex would be enforcing two grammars, and
// the failure mode is silent: a page that merges and then 404s in public.
//
// The dot-free alphabet is load-bearing, not cosmetic. It is what makes the
// planned P2 `/docs/<slug>.md` raw-markdown suffix unambiguous by construction
// — a path ending in `.md` can never itself be a page slug, so the family never
// needs a disambiguation rule.
//
// Changing either constant changes a ratified public URL contract: ADR 0049
// calls the grammar a one-way door that survives P3, so an edit here needs a
// new decision, not a code review.

/**
 * ADR 0049 §1 — lowercase alphanumerics in single-hyphen-separated runs. No
 * leading, trailing, or doubled hyphens; no dots; no uppercase.
 */
export const DOCS_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * ADR 0049 §1 — the fixed routes and index files the `/docs` family serves (or
 * will serve) that no published page may shadow. Three of the five are outside
 * `DOCS_SLUG_PATTERN` already (they carry dots), and they are still listed
 * here: the reservation is about the served URL path, not about the slug
 * alphabet, and a future grammar relaxation must not quietly un-reserve them.
 */
export const RESERVED_DOCS_SLUGS: readonly string[] = [
  "agents",
  "assets",
  "sitemap.xml",
  "llms.txt",
  "llms-full.txt",
  "robots.txt",
  "index",
  // LAT-1611 (LAT-1600 S12) — the docs search route. Added to ADR 0049 §1's
  // list in the same change that shipped the route, for the reason the list
  // exists: a fixed route that is NOT reserved is a route a `help/search.md`
  // page can merge against and then 404 in public, and the content-law gate
  // would have no way to know. The reservation and the router read this one
  // definition, so the two enforcement points cannot disagree about it.
  "search",
  "mcp.html",
];

/** True when `slug` is inside the ADR 0049 §1 page-slug grammar. */
export function isDocsSlug(slug: string): boolean {
  return DOCS_SLUG_PATTERN.test(slug);
}

/** True when `slug` names a reserved `/docs` route and so cannot be a page. */
export function isReservedDocsSlug(slug: string): boolean {
  return RESERVED_DOCS_SLUGS.includes(slug);
}
